import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "./AuthContext";

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0 });

  /* -----------------------------------------------------------
     1️⃣  VIDER le panier au chargement - TOUJOURS
  ----------------------------------------------------------- */
  useEffect(() => {
    // ⚠️ TOUJOURS commencer avec un panier vide
    const emptyCart = {
      items: [],
      total: 0,
      userId: user?.id,
      createdAt: new Date().toISOString(),
    };
    
    setCart(emptyCart);
    
    // Nettoyer le localStorage
    if (user) {
      localStorage.removeItem(`cart_${user.id}`);
    } else {
      localStorage.removeItem("temp_cart");
    }
    
    console.log("🔄 Panier vidé au chargement de la page");
  }, []); // ⚠️ Seulement au montage du composant

  /* -----------------------------------------------------------
     2️⃣  Sauvegarde du panier (uniquement pendant la session)
  ----------------------------------------------------------- */
  const saveCart = (cartData) => {
    // ⚠️ NE PAS sauvegarder dans localStorage pour persistance
    // Le panier sera perdu au rechargement
    console.log("💾 Sauvegarde session (non persistante):", cartData.items.length, "articles");
  };

  /* -----------------------------------------------------------
     3️⃣  Ajout au panier
  ----------------------------------------------------------- */
  const addToCart = (product, quantity = 1) => {
    setCart((prev) => {
      const index = prev.items.findIndex((i) => i.id === product.id);
      let items;

      // Normaliser les images une seule fois
      const normalizedProduct = {
        ...product,
        image: product.image ?? null,
        images: product.images ?? null,
        image_url: product.image_url ?? null,
      };

      if (index >= 0) {
        // produit existe → augmenter quantité
        items = prev.items.map((item, i) =>
          i === index ? { ...item, quantity: item.quantity + quantity } : item
        );
      } else {
        // nouveau produit
        items = [
          ...prev.items,
          {
            ...normalizedProduct,
            quantity,
            addedAt: new Date().toISOString(),
          },
        ];
      }

      const total = items.reduce((t, item) => t + item.price * item.quantity, 0);

      const newCart = { 
        ...prev, 
        items, 
        total, 
        userId: user?.id,
        lastUpdated: new Date().toISOString()
      };
      
      saveCart(newCart);

      return newCart;
    });
  };

  /* -----------------------------------------------------------
     4️⃣  Suppression
  ----------------------------------------------------------- */
  const removeFromCart = (productId) => {
    setCart((prev) => {
      const items = prev.items.filter((i) => i.id !== productId);
      const total = items.reduce((t, item) => t + item.price * item.quantity, 0);

      const newCart = { 
        ...prev, 
        items, 
        total,
        lastUpdated: new Date().toISOString()
      };
      saveCart(newCart);

      return newCart;
    });
  };

  /* -----------------------------------------------------------
     5️⃣  Mise à jour quantité
  ----------------------------------------------------------- */
  const updateQuantity = (productId, quantity) => {
    if (quantity < 1) return removeFromCart(productId);

    setCart((prev) => {
      const items = prev.items.map((i) =>
        i.id === productId ? { ...i, quantity } : i
      );
      const total = items.reduce((t, item) => t + item.price * item.quantity, 0);

      const newCart = { 
        ...prev, 
        items, 
        total,
        lastUpdated: new Date().toISOString()
      };
      saveCart(newCart);

      return newCart;
    });
  };

  /* -----------------------------------------------------------
     6️⃣  Vider panier
  ----------------------------------------------------------- */
  const clearCart = () => {
    const emptyCart = { 
      items: [], 
      total: 0, 
      userId: user?.id,
      clearedAt: new Date().toISOString()
    };
    setCart(emptyCart);
    saveCart(emptyCart);
    console.log("🗑️ Panier vidé manuellement");
  };

  /* -----------------------------------------------------------
     7️⃣  Infos utilitaires
  ----------------------------------------------------------- */
  const getCartItemCount = () =>
    cart.items.reduce((t, item) => t + item.quantity, 0);

  const getCurrentCart = () => cart;

  // 🔄 Optionnel: Vider le panier quand l'utilisateur se déconnecte
  useEffect(() => {
    if (!user) {
      const emptyCart = { items: [], total: 0 };
      setCart(emptyCart);
      console.log("🚪 Panier vidé à la déconnexion");
    }
  }, [user]);

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartItemCount,
        getCurrentCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};